import { formatDate, formatNumber } from '@angular/common';
import { defineMasterdataEntity } from '../types/masterdata-entity-definition';
import {
  MasterdataProduct,
  MasterdataProductReference,
} from '../types/masterdata-entities';
import {
  productDecimalDigits,
  ProductDecimalsService,
} from '../services/product-decimals.service';
import { BookmarkSubCategory } from '@softapps/core';
import { ProductDetailPanelComponent } from '@softapps/core/product-info';

function stockBadge(p: MasterdataProduct) {
  const stock = p.stockQuantity;
  if (stock == null) return null;
  return stock.amount > 0
    ? { label: 'Auf Lager', tone: 'success' as const }
    : { label: 'Nicht verfügbar', tone: 'danger' as const };
}

function stockLabel(stock: MasterdataProduct['stockQuantity']): string | null {
  if (stock == null) return null;
  const d = productDecimalDigits().quantity;
  return `${formatNumber(stock.amount, 'de', `1.${d}-${d}`)} ${stock.unit ?? ''}`.trim();
}

/** Amount only — the unit is already on the Lagerstand next to it. */
function stockAmountLabel(stock: MasterdataProduct['stockQuantity']): string | null {
  if (stock == null) return null;
  const d = productDecimalDigits().quantity;
  return formatNumber(stock.amount, 'de', `1.${d}-${d}`);
}

/** Lagerstand and, when the backend supplies it, the Dispostand behind it. */
function rowStockLabel(p: MasterdataProduct): string | null {
  const stock = stockLabel(p.stockQuantity);
  const planned = stockAmountLabel(p.plannedStockQuantity);
  if (!stock) return planned ? `Dispo ${planned}` : null;
  return planned ? `${stock} · Dispo ${planned}` : stock;
}

/** Listungskennzeichen of the article. Only `J`/`N` are contract-documented
    (see MasterdataProduct.listingStatus), so every other code is surfaced
    verbatim instead of being translated into a wrong word.
    TODO: label the customer codes (Auslaufartikel etc.) once the backend
    confirms the catalogue — ClickUp 869envzfz covers the translation. */
function listingBadge(p: MasterdataProduct) {
  const code = p.listingStatus?.trim();
  if (!code || code.toUpperCase() === 'J') return null;
  if (code.toUpperCase() === 'N') {
    return { label: 'Nicht gelistet', tone: 'danger' as const };
  }
  return { label: `Listung ${code}`, tone: 'warning' as const };
}

function priceLabel(value: number | null | undefined): string | null {
  if (value == null) return null;
  const d = productDecimalDigits().price;
  return formatNumber(value, 'de', `1.${d}-${d}`);
}

function dateLabel(iso: string | null | undefined): string | null {
  return iso ? formatDate(iso, 'dd.MM.yyyy', 'de') : null;
}

function negativeTone(value: number | null | undefined): 'danger' | null {
  return value != null && value < 0 ? 'danger' : null;
}

function refLabel(ref: MasterdataProductReference | null | undefined): string | null {
  if (!ref) return null;
  if (ref.key && ref.name) return `${ref.key} - ${ref.name}`;
  return ref.name ?? ref.key ?? null;
}

export const MASTERDATA_PRODUCT = defineMasterdataEntity<MasterdataProduct>({
  name: 'product',
  responseKey: 'products',
  defaultFilter: [{ type: 'booleanIsFalse', name: 'deleted' }],
  displayWith: (p) => {
    const name = [p.description1, p.description2].filter(Boolean).join(' ');
    const number = p.key ?? '';
    if (number && name) return `${number} - ${name}`;
    return number || name;
  },
  trackWith: (p) => p.key ?? p.id ?? '',
  secondaryWith: (p) => p.matchcode ?? p.description3 ?? null,
  unitWith: rowStockLabel,
  badgesWith: (p) =>
    [stockBadge(p), listingBadge(p)].filter(
      (badge): badge is NonNullable<typeof badge> => badge !== null
    ),
  bookmarkSubCategory: BookmarkSubCategory.Product,
  init: (injector) => injector.get(ProductDecimalsService).load(),
  icon: 'fa-regular fa-box',
  title: 'Artikelsuche',
  label: 'Artikel',
  placeholder: 'Artikel suchen...',
  detailSidebar: true,
  detailComponent: () => ProductDetailPanelComponent,
  detailInputs: (p) => ({
    item: {
      product: {
        id: p.id,
        key: p.key,
        description1:
          [p.description1, p.description2].filter(Boolean).join(' ') ||
          p.shortDescription,
        description2: p.matchcode ?? p.description3,
      },
    },
  }),
  columns: [
    {
      label: 'Artikel',
      type: 'entity',
      icon: 'fa-regular fa-box',
      value: (p) =>
        [p.description1, p.description2].filter(Boolean).join(' ') ||
        p.shortDescription,
      secondary: (p) => p.matchcode ?? p.description3 ?? null,
    },
    {
      label: 'Lagerstand',
      value: (p) => stockLabel(p.stockQuantity),
      align: 'end',
      tone: (p) => negativeTone(p.stockQuantity?.amount),
    },
    {
      label: 'Dispostand',
      value: (p) => stockLabel(p.plannedStockQuantity),
      align: 'end',
      tone: (p) => negativeTone(p.plannedStockQuantity?.amount),
    },
    {
      label: 'Lagerstand gesamt',
      value: (p) => stockLabel(p.totalStockQuantity),
      align: 'end',
      tone: (p) => negativeTone(p.totalStockQuantity?.amount),
    },
    {
      label: 'Dispostand gesamt',
      value: (p) => stockLabel(p.totalPlannedStockQuantity),
      align: 'end',
      tone: (p) => negativeTone(p.totalPlannedStockQuantity?.amount),
    },
    { label: 'Art-Nr.', value: (p) => p.key },
    { label: 'EAN', value: (p) => p.ean ?? null },
    { label: 'Matchcode', value: (p) => p.matchcode },
    { label: 'Kurzbezeichnung', value: (p) => p.shortDescription },
    {
      label: 'Hauptlieferant',
      value: (p) =>
        (p.suppliers?.find((s) => s.mainSupplier) ?? p.suppliers?.[0])?.name ?? null,
    },
    {
      label: 'Lief.-Art-Nr.',
      value: (p) =>
        (p.suppliers?.find((s) => s.mainSupplier) ?? p.suppliers?.[0])
          ?.productNumber ?? null,
    },
    { label: 'Produktgruppe', value: (p) => refLabel(p.productGroup) },
    { label: 'Hauptgruppe', value: (p) => refLabel(p.mainGroup) },
    { label: 'Untergruppe', value: (p) => refLabel(p.subGroup) },
    { label: 'Untergruppe 2', value: (p) => refLabel(p.subGroup2) },
    { label: 'Konditionsgruppe', value: (p) => refLabel(p.conditionGroup) },
    { label: 'Verrechnungseinheit', value: (p) => refLabel(p.consumerUnit) },
    { label: 'Lagereinheit', value: (p) => refLabel(p.stockUnit) },
    {
      label: 'VK exkl.',
      value: (p) => priceLabel(p.salesPrice?.excl),
      align: 'end',
      tone: (p) => negativeTone(p.salesPrice?.excl),
    },
    {
      label: 'VK inkl.',
      value: (p) => priceLabel(p.salesPrice?.incl),
      align: 'end',
      tone: (p) => negativeTone(p.salesPrice?.incl),
    },
    {
      label: 'EK',
      value: (p) => priceLabel(p.purchasePrice?.value ?? p.purchasePrice?.excl),
      align: 'end',
      tone: (p) => negativeTone(p.purchasePrice?.value ?? p.purchasePrice?.excl),
    },
    { label: 'Listung', value: (p) => p.listingStatus ?? null },
    { label: 'Artikelart', value: (p) => p.type ?? null },
    { label: 'Letzte Änderung', value: (p) => dateLabel(p.lastChangeDate) },
  ],
});
